import React from 'react';
import { Check, Circle, AlertTriangle, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Helper to check if AI actually processed the note (has real data)
const hasAIProcessedData = (note, isRelationshipHistory, isAccountHistory) => {
  // Check ai_structured_data has actual content
  if (note?.ai_structured_data && Object.keys(note.ai_structured_data).length > 0) {
    if (isRelationshipHistory) {
      // For relationship_history, check for relational_analysis
      return !!note.ai_structured_data.relational_analysis || !!note.ai_structured_data.context_summary;
    }
    if (isAccountHistory) {
      // For account_history, check for strategic_analysis
      return !!note.ai_structured_data.strategic_analysis || !!note.ai_structured_data.executive_summary;
    }
    return true;
  }
  
  // Fallback checks for older data
  if (isRelationshipHistory) {
    return !!note?.relationship_history_ai_result;
  }
  
  if (isAccountHistory) {
    return !!note?.account_history_ai_result;
  }
  
  // For activity type
  return !!note?.structured_summary?.key_points?.length || 
         !!note?.extracted_tasks_preview?.length ||
         !!note?.activity_ai_result;
};

const VoiceNoteActivityProgress = ({ note }) => {
  // Solo mostrar para notas clasificadas como "activity", "relationship_history" o "account_history"
  if (note?.classified_as !== 'activity' && note?.classified_as !== 'relationship_history' && note?.classified_as !== 'account_history') return null;

  const isRelationshipHistory = note?.classified_as === 'relationship_history';
  const isAccountHistory = note?.classified_as === 'account_history';
  const aiProcessed = hasAIProcessedData(note, isRelationshipHistory, isAccountHistory);
  
  // Define steps based on category
  const steps = isRelationshipHistory ? [
    {
      id: 'recorded',
      label: 'Audio grabado',
      completed: !!note?.audio_url,
    },
    {
      id: 'transcribed',
      label: 'Transcrito',
      completed: !!note?.transcript_text,
    },
    {
      id: 'processed',
      label: 'IA procesado',
      completed: aiProcessed,
    },
    {
      id: 'linked',
      label: 'Vinculado a contacto',
      completed: !!note?.linked_contact_id,
    },
  ] : isAccountHistory ? [
    {
      id: 'recorded',
      label: 'Audio grabado',
      completed: !!note?.audio_url,
    },
    {
      id: 'transcribed',
      label: 'Transcrito',
      completed: !!note?.transcript_text,
    },
    {
      id: 'processed',
      label: 'IA procesado',
      completed: aiProcessed,
    },
    {
      id: 'linked',
      label: 'Vinculado a empresa/TYPSA',
      completed: !!note?.linked_company_id || !!note?.linked_typsa_org_unit_id,
    },
  ] : [
    {
      id: 'recorded',
      label: 'Audio grabado',
      completed: !!note?.audio_url,
    },
    {
      id: 'transcribed',
      label: 'Transcrito',
      completed: !!note?.transcript_text,
    },
    {
      id: 'processed',
      label: 'Procesado con IA',
      completed: aiProcessed,
    },
    {
      id: 'linked',
      label: 'Vinculado a actividad',
      completed: !!note?.linked_activity_id,
    },
  ];

  const completedSteps = steps.filter(s => s.completed).length;
  const isFullyProcessed = isRelationshipHistory 
    ? !!note?.linked_contact_id 
    : isAccountHistory 
    ? (!!note?.linked_company_id || !!note?.linked_typsa_org_unit_id)
    : !!note?.linked_activity_id;
  const hasUncratedTasks = !isRelationshipHistory && !isAccountHistory && note?.extracted_tasks_preview?.length > 0 && !note?.tasks_generated;

  const title = isRelationshipHistory 
    ? 'Progreso de Inteligencia Relacional' 
    : isAccountHistory 
    ? 'Progreso de Análisis de Cuenta'
    : 'Progreso de Documentación';

  return (
    <Card data-testid="activity-progress-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {(isRelationshipHistory || isAccountHistory) && <User className="w-4 h-4" />}
            {title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {isFullyProcessed ? (
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                {isRelationshipHistory ? 'Vinculado' : 'Procesada'}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                Pendiente
              </Badge>
            )}
            {hasUncratedTasks && (
              <Badge variant="outline" className="text-orange-600 border-orange-300 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Tareas sin crear
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progreso</span>
            <span>{completedSteps}/{steps.length} pasos</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${isFullyProcessed ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${(completedSteps / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Steps list */}
        <div className="space-y-2">
          {steps.map((step, index) => (
            <div 
              key={step.id}
              className={`flex items-center gap-3 p-2 rounded-lg ${
                step.completed ? 'bg-green-50 dark:bg-green-900/20' : 'bg-muted/50'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                step.completed 
                  ? 'bg-green-500 text-white' 
                  : 'bg-muted-foreground/20 text-muted-foreground'
              }`}>
                {step.completed ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="text-xs font-medium">{index + 1}</span>
                )}
              </div>
              <span className={`text-sm ${step.completed ? 'text-green-700 dark:text-green-300 font-medium' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        {/* Next action hint */}
        {!isFullyProcessed && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Siguiente paso:</strong>{' '}
              {!note?.transcript_text 
                ? 'Transcribir el audio'
                : !aiProcessed
                ? 'Procesar con IA'
                : isRelationshipHistory 
                  ? 'Vincular a un contacto (nuevo o existente)'
                  : isAccountHistory
                  ? 'Vincular a una empresa o unidad TYPSA'
                  : 'Vincular a una actividad (nueva o existente)'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VoiceNoteActivityProgress;
